import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, MessageSquare, Mail, MapPin, Calendar, Facebook, Instagram, Clock } from 'lucide-react';
import { SITE, NAV } from '@/data/site-config';

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-30" style={{ background: scrolled ? 'rgba(9,11,14,0.95)' : 'rgba(9,11,14,0.7)', borderBottom: `1px solid ${scrolled ? 'var(--ft-border)' : 'transparent'}`, backdropFilter: 'blur(14px)', transition: 'all .2s' }}>
      <a href="#main" className="skip-link">Skip to main content</a>

      {/* Top utility bar — only on desktop (hidden on mobile to save space) */}
      <div className="hidden lg:block border-b border-[color:var(--ft-border)]" style={{ background: 'rgba(0,0,0,0.35)' }}>
        <div className="container-x flex items-center justify-between text-xs" style={{ height: 36 }}>
          <div className="flex items-center gap-5 text-[color:var(--ft-muted)]">
            <a href={`tel:${SITE.phoneE164}`} className="inline-flex items-center gap-1.5 hover:text-[color:var(--ft-text)]"><Phone size={12} /> {SITE.phone}</a>
            <a href={`mailto:${SITE.email}`} className="inline-flex items-center gap-1.5 hover:text-[color:var(--ft-text)]"><Mail size={12} /> {SITE.email}</a>
            <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {SITE.hoursLabel}</span>
            <a href="https://maps.google.com/?q=3850+SE+Dixie+Highway+Stuart+FL+34997" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-[color:var(--ft-text)]"><MapPin size={12} /> {SITE.address.line1}, {SITE.city}, {SITE.stateAbbr}</a>
          </div>
          <div className="flex items-center gap-2">
            <a href={SITE.social.facebookOwner} target="_blank" rel="noreferrer" aria-label="Connect with Jarad on Facebook" className="fb-chip" data-testid="nav-fb-jarad"><Facebook size={14} fill="#fff" /> Connect with Jarad</a>
            <a href={SITE.social.facebook} target="_blank" rel="noreferrer" aria-label="Fitness Training 360 on Facebook" className="text-[color:var(--ft-muted)] hover:text-[#1877F2] transition" title="FT360 Facebook"><Facebook size={16} /></a>
            <a href={SITE.social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="text-[color:var(--ft-muted)] hover:text-[color:var(--ft-accent)]"><Instagram size={16} /></a>
          </div>
        </div>
      </div>

      {/* Main header bar — tall, with prominent logo */}
      <div className="container-x flex items-center justify-between gap-4" style={{ minHeight: scrolled ? 84 : 104, transition: 'min-height .2s' }}>
        <Link to="/" className="flex items-center gap-3 shrink-0" aria-label={`${SITE.name} home`} data-testid="nav-logo">
          <Logo size={scrolled ? 60 : 76} />
          <div className="hidden sm:block leading-tight">
            <div className="font-display font-bold text-[18px] lg:text-[20px] tracking-tight">{SITE.name}</div>
            <div className="text-[11px] lg:text-[12px] text-[color:var(--ft-accent)] uppercase tracking-[0.15em] font-semibold">Private Studio · {SITE.city}, {SITE.stateAbbr}</div>
          </div>
        </Link>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-1">
          {NAV.map((it) => (
            <Link key={it.href} to={it.href} className="px-3 py-2 rounded-lg text-[15px] font-medium text-[color:var(--ft-muted)] hover:text-[color:var(--ft-text)] hover:bg-[color:var(--ft-surface)] transition" data-testid={`nav-${it.label.toLowerCase()}`}>{it.label}</Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2 shrink-0">
          <a href={SITE.booking.url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" data-testid="nav-book"><Calendar size={14} /> Book</a>
          <Link to="/contact" className="btn btn-primary btn-sm" data-testid="nav-cta">Free Consultation</Link>
        </div>

        <button className="lg:hidden btn btn-ghost btn-sm" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="nav-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[color:var(--ft-border)] bg-[color:var(--ft-bg-2)]" data-testid="nav-mobile">
          <div className="container-x py-4 grid gap-1">
            {NAV.map((it) => (
              <Link key={it.href} to={it.href} className="px-3 py-3 rounded-lg text-[16px] font-medium text-[color:var(--ft-text)] hover:bg-[color:var(--ft-surface)]">{it.label}</Link>
            ))}
            <div className="grid grid-cols-3 gap-2 pt-3">
              <a href={`tel:${SITE.phoneE164}`} className="btn btn-secondary btn-sm"><Phone size={14} /> Call</a>
              <a href={`sms:${SITE.phoneE164}`} className="btn btn-secondary btn-sm"><MessageSquare size={14} /> Text</a>
              <a href={SITE.booking.url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm"><Calendar size={14} /> Book</a>
            </div>
            <Link to="/contact" className="btn btn-primary mt-2">Free Consultation</Link>
            <a href={SITE.social.facebookOwner} target="_blank" rel="noreferrer" className="fb-chip justify-center mt-2 w-full" data-testid="mobile-fb-jarad"><Facebook size={16} fill="#fff" /> Connect with Jarad on Facebook</a>
            <div className="flex items-center gap-3 pt-3 text-[color:var(--ft-muted)] text-sm">
              <a href={SITE.social.facebook} target="_blank" rel="noreferrer" aria-label="FT360 Facebook" className="hover:text-[#1877F2]"><Facebook size={18} /></a>
              <a href={SITE.social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-[color:var(--ft-text)]"><Instagram size={18} /></a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export function Logo({ size = 56 }) {
  // The official FT360 logo is on a black background — render on a matching surface so it blends seamlessly.
  return (
    <span
      className="inline-flex items-center justify-center shrink-0 overflow-hidden"
      style={{
        height: size,
        width: size * 1.45, // logo aspect ~3:2
        borderRadius: 12,
        background: '#000',
        border: '1px solid rgba(200,255,0,0.22)',
        boxShadow: '0 6px 18px -8px rgba(200,255,0,0.30)',
      }}
      aria-hidden="true"
    >
      <img
        src={SITE.logo.src}
        alt=""
        width={SITE.logo.width}
        height={SITE.logo.height}
        loading="eager"
        decoding="async"
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
      />
    </span>
  );
}
